import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import { fetchConversationFilterCounts } from "../api/client";
import { useSocketContext } from "../context/SocketContext";
import { useAuthContext } from "../context/AuthContext";
import { workspaceScopedQueryKey } from "../utils/workspaceQueryScope";
import { getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import { loadLastSeenClientAtMap } from "../utils/chatUnreadUtils";
import { buildConversationFilterCountsRequest } from "../pages/Chat/utils/chatListContext";
import useDebouncedValue from "./useDebouncedValue";

export const CONVERSATION_FILTER_COUNTS_KEY = "conversationFilterCounts";

const POLL_MS = 30_000;
const STALE_MS = 12_000;
const SOCKET_DEBOUNCE_MS = 4_000;

export function invalidateConversationFilterCounts(queryClient) {
  if (!queryClient) return;
  queryClient.invalidateQueries(CONVERSATION_FILTER_COUNTS_KEY);
}

function isTransientAuthFailure(error) {
  const status = error?.response?.status ?? error?.status;
  const code = error?.response?.data?.code ?? error?.code;
  return (
    status === 503 ||
    code === "errors.auth.temporarily_unavailable" ||
    code === "errors.auth.forbidden"
  );
}

/**
 * Contagens por chip do atendimento, cruzando a combinação ativa (o que a lista mostra).
 * Debounce alinhado à lista; query própria para não bloquear o GET /conversations.
 */
export function useConversationFilterCounts({ filters, advancedFilterState } = {}) {
  const queryClient = useQueryClient();
  const socket = useSocketContext()?.socket;
  const { bearerToken } = useAuthContext() || {};
  const tenantSlug = getActiveWorkspaceSlug();
  const canFetch = Boolean(bearerToken && tenantSlug);

  const debouncedFilters = useDebouncedValue(filters || [], 400);
  const request = useMemo(
    () => buildConversationFilterCountsRequest(debouncedFilters, advancedFilterState),
    [debouncedFilters, advancedFilterState]
  );

  const queryKey = workspaceScopedQueryKey(
    CONVERSATION_FILTER_COUNTS_KEY,
    request.filters || [],
    request.advanced || {}
  );

  const { data } = useQuery(
    queryKey,
    async () => {
      const res = await fetchConversationFilterCounts({
        filters: request.filters,
        advanced: request.advanced,
        atendimentoLastSeen: loadLastSeenClientAtMap(),
      });
      if (res?.timeout) {
        const previous = queryClient.getQueryData(queryKey);
        if (previous?.byLabel && Object.keys(previous.byLabel).length) {
          return previous;
        }
      }
      return res;
    },
    {
      enabled: canFetch,
      staleTime: STALE_MS,
      refetchInterval: canFetch ? POLL_MS : false,
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      retry: (failureCount, error) => {
        if (!isTransientAuthFailure(error)) return failureCount < 1;
        return failureCount < 3;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    }
  );

  useEffect(() => {
    if (!socket) return undefined;
    let timer = null;
    const schedule = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        queryClient.invalidateQueries(CONVERSATION_FILTER_COUNTS_KEY);
      }, SOCKET_DEBOUNCE_MS);
    };
    socket.on("message", schedule);
    socket.on("request", schedule);
    socket.on("messagesReload", schedule);
    return () => {
      if (timer) clearTimeout(timer);
      socket.off("message", schedule);
      socket.off("request", schedule);
      socket.off("messagesReload", schedule);
    };
  }, [queryClient, socket]);

  return useMemo(
    () => ({
      countsByLabel: data?.timeout ? {} : data?.byLabel || {},
      unreadCapped: Boolean(data?.unreadCapped),
    }),
    [data]
  );
}
